import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tags, BadgeCheck, Clock } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsolePricing, formatFcfa, type ConsolePriceRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA customer price book (Epic 2) — the sales-facing tariff grid. Rows are
// per-SKU, per-shop-type CUSTOMER prices only; cost and margin stay locked to
// the finance scope (RLS, migrations 131-140). A non-authorized JWT sees nothing.
// ---------------------------------------------------------------------------

const SHOP_TYPE_LABELS: Record<string, string> = {
  retail: "Détail",
  semi: "Semi-gros",
  gros: "Gros",
  pro: "Pro",
};

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function PriceBook() {
  const q = useQuery({ queryKey: ["djayma", "console-pricing"], queryFn: fetchConsolePricing, retry: false });

  const rows: ConsolePriceRow[] = q.data ?? [];
  const approvedCount = rows.filter((r) => r.approved).length;
  const pendingCount = rows.length - approvedCount;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Tags className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Grille tarifaire</h1>
            <p className="text-sm text-stone-500">
              Prix de vente clients par type de boutique — le coût et la marge restent réservés à la finance.
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Grille tarifaire non joignable. Vérifiez les migrations 131-140 et l&apos;exposition du schéma
            <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Kpi
            icon={<Tags className="h-5 w-5" />}
            tone="bg-stone-100 text-stone-700"
            label="Lignes de prix"
            value={rows.length}
          />
          <Kpi
            icon={<BadgeCheck className="h-5 w-5" />}
            tone="bg-emerald-100 text-emerald-700"
            label="Tarifs approuvés"
            value={approvedCount}
          />
          <Kpi
            icon={<Clock className="h-5 w-5" />}
            tone="bg-amber-100 text-amber-700"
            label="En attente d'approbation"
            value={pendingCount}
          />
        </div>

        {/* Price book table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Tarifs clients</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucun tarif.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Produit</th>
                    <th className="py-2 pr-4 font-medium">Marque</th>
                    <th className="py-2 pr-4 font-medium">Type boutique</th>
                    <th className="py-2 pr-4 font-medium text-right">Prix</th>
                    <th className="py-2 pr-4 font-medium">Depuis</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={`${r.sku}-${r.shop_type}`}>
                      <td className="py-2 pr-4">
                        <span className="text-stone-700">{r.product}</span>
                        <span className="block text-xs text-stone-400">{r.sku}</span>
                      </td>
                      <td className="py-2 pr-4 text-stone-500">{r.brand}</td>
                      <td className="py-2 pr-4 text-stone-500">{SHOP_TYPE_LABELS[r.shop_type] ?? r.shop_type}</td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{formatFcfa(r.price_xof)}</td>
                      <td className="py-2 pr-4 text-stone-500">
                        {new Date(r.effective_from).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="py-2 pr-4">
                        {r.approved ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Approuvé
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                            En attente
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
